import { useEffect, useState } from "react";
import { Button, Checkbox, DatePicker, Input, Select, Switch, message } from "antd";
import moment from "moment";
import type { Moment } from "moment";
import CountryDropdown from "../../account/misc/CountryDropdown";
import { UnitedStates } from "../../../util/countries";
import { getUSStates } from "../../../util/utils";
import YextApi, { ensureHttps, yextErrorMessage } from "../../../api/YextApi";
import type { YextStaffBio } from "../../../api/YextApi";
import "./ProfileIdentitySection.scss";

const { TextArea } = Input;

// Business-type chips under the Business Identity header (distinct from the broader
// Business Categories list below).
const CATEGORY_OPTIONS = ["Restaurant", "Bar", "Food Truck"];
const BUSINESS_CATEGORIES = ["Restaurant", "Hospitality", "Retail"];

// Static list — built once rather than on every render.
const US_STATE_OPTIONS = getUSStates().map((s) => ({ value: s.key, label: s.value }));

interface IdentityForm {
  businessName: string;
  alternateNames: string;
  category: string[];
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  suppressAddress: boolean;
  displayAddress: string;
  latitude: string;
  longitude: string;
  primaryPhone: string;
  secondaryPhone: string;
  email: string;
  website: string;
  websiteDisplayUrl: string;
  descriptionShort: string;
  descriptionLong: string;
  featuredMessage: string;
  categories: string[];
  openingDate: Moment | null;
  staffBios: YextStaffBio[];
}

const EMPTY_BIO: YextStaffBio = { name: "", bio: "" };

const EMPTY: IdentityForm = {
  businessName: "",
  alternateNames: "",
  category: [],
  street: "",
  street2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  suppressAddress: false,
  displayAddress: "",
  latitude: "",
  longitude: "",
  primaryPhone: "",
  secondaryPhone: "",
  email: "",
  website: "",
  websiteDisplayUrl: "",
  descriptionShort: "",
  descriptionLong: "",
  featuredMessage: "",
  categories: [],
  openingDate: null,
  // Always render at least one (empty) staff-bio entry.
  staffBios: [{ ...EMPTY_BIO }],
};

interface Props {
  locationId?: number;
  onDirtyChange?: (dirty: boolean) => void;
  // Label for the primary action (e.g. "Start now" during first-time onboarding).
  saveLabel?: string;
  // Hides the Discard button (used in the onboarding flow, which shows a single action).
  hideDiscard?: boolean;
  // Fired after a successful save — used by onboarding to advance once the entity exists.
  onSaved?: () => void;
}

export function ProfileIdentitySection({
  locationId,
  onDirtyChange,
  saveLabel = "Save Changes",
  hideDiscard = false,
  onSaved,
}: Props) {
  const [form, setForm] = useState<IdentityForm>(EMPTY);
  const [savedSnapshot, setSavedSnapshot] = useState<IdentityForm>(EMPTY);
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(form) !== JSON.stringify(savedSnapshot);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      const loaded: IdentityForm = {
        businessName: data.businessName ?? "",
        alternateNames: data.alternateNames ?? "",
        category: data.category ?? [],
        street: data.street ?? "",
        street2: data.street2 ?? "",
        city: data.city ?? "",
        state: data.state ?? "",
        zip: data.zip ?? "",
        country: data.country ?? "",
        suppressAddress: data.suppressAddress ?? false,
        displayAddress: data.displayAddress ?? "",
        latitude: data.latitude ?? "",
        longitude: data.longitude ?? "",
        primaryPhone: data.primaryPhone ?? "",
        secondaryPhone: data.secondaryPhone ?? "",
        email: data.email ?? "",
        website: data.website ?? "",
        websiteDisplayUrl: data.websiteDisplayUrl ?? "",
        descriptionShort: data.descriptionShort ?? "",
        descriptionLong: data.descriptionLong ?? "",
        featuredMessage: data.featuredMessage ?? "",
        categories: data.categories ?? [],
        openingDate: data.openingDate ? moment(data.openingDate, "YYYY-MM-DD") : null,
        staffBios:
          data.staffBios && data.staffBios.length > 0
            ? data.staffBios.map((b) => ({ name: b.name ?? "", bio: b.bio ?? "" }))
            : [{ ...EMPTY_BIO }],
      };
      setForm(loaded);
      // First-time setup: the form is pre-filled from the base Location but nothing is saved yet, so
      // treat it as unsaved (dirty) — keeps Save / "Start now" enabled. A saved profile is the baseline.
      setSavedSnapshot(data.existing ? loaded : EMPTY);
    });
  }, [locationId]);

  function update<K extends keyof IdentityForm>(key: K, value: IdentityForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleInList(key: "category" | "categories", value: string, checked: boolean) {
    setForm((f) => ({
      ...f,
      [key]: checked ? [...f[key], value] : f[key].filter((c) => c !== value),
    }));
  }

  function updateStaffBio(index: number, key: keyof YextStaffBio, value: string) {
    setForm((f) => ({
      ...f,
      staffBios: f.staffBios.map((b, i) => (i === index ? { ...b, [key]: value } : b)),
    }));
  }

  function addStaffBio() {
    setForm((f) => ({ ...f, staffBios: [...f.staffBios, { ...EMPTY_BIO }] }));
  }

  function removeStaffBio(index: number) {
    setForm((f) => {
      const next = f.staffBios.filter((_, i) => i !== index);
      return { ...f, staffBios: next.length > 0 ? next : [{ ...EMPTY_BIO }] };
    });
  }

  function discardChanges() {
    setForm(savedSnapshot);
  }

  async function saveChanges() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setSaving(true);
    try {
      await YextApi.syncLocation(locationId, {
        businessName: form.businessName,
        alternateNames: form.alternateNames,
        category: form.category,
        street: form.street,
        street2: form.street2,
        city: form.city,
        state: form.state,
        zip: form.zip,
        country: form.country,
        suppressAddress: form.suppressAddress,
        displayAddress: form.displayAddress,
        latitude: form.latitude,
        longitude: form.longitude,
        primaryPhone: form.primaryPhone,
        secondaryPhone: form.secondaryPhone,
        email: form.email,
        website: form.website,
        websiteDisplayUrl: form.websiteDisplayUrl,
        descriptionShort: form.descriptionShort,
        descriptionLong: form.descriptionLong,
        featuredMessage: form.featuredMessage,
        categories: form.categories,
        openingDate: form.openingDate ? form.openingDate.format("YYYY-MM-DD") : undefined,
        // Drop empty bio entries; trim what remains. Empty array clears the Yext bios list.
        staffBios: form.staffBios
          .map((b) => ({ name: b.name.trim(), bio: b.bio.trim() }))
          .filter((b) => b.name.length > 0 || b.bio.length > 0),
      });
      setSavedSnapshot(form);
      message.success("Saved!");
      onSaved?.();
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save identity."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="profile-identity">
      <section className="pi-group">
        <h3 className="pi-group-title">Business Identity</h3>

        <Field label="Business Name">
          <Input
            placeholder="Business Name"
            size="large"
            value={form.businessName}
            onChange={(e) => update("businessName", e.target.value)}
          />
        </Field>

        <Field label="Alternate Business Names">
          <Input
            placeholder="Alternate Business Names"
            size="large"
            value={form.alternateNames}
            onChange={(e) => update("alternateNames", e.target.value)}
          />
        </Field>

        <div className="pi-categories">
          <span className="pi-field-label">Category</span>
          <div className="pi-categories-inline">
            {CATEGORY_OPTIONS.map((cat) => (
              <Checkbox
                key={cat}
                checked={form.category.includes(cat)}
                onChange={(e) => toggleInList("category", cat, e.target.checked)}
              >
                {cat}
              </Checkbox>
            ))}
          </div>
        </div>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">Location</h3>

        <Field label="Street Address">
          <Input
            placeholder="Address"
            size="large"
            value={form.street}
            onChange={(e) => update("street", e.target.value)}
          />
        </Field>

        <Field label="Street Address Line 2 (Optional)">
          <Input
            placeholder="Address Line 2"
            size="large"
            value={form.street2}
            onChange={(e) => update("street2", e.target.value)}
          />
        </Field>

        <div className="pi-row">
          <Field label="City">
            <Input
              placeholder="City"
              size="large"
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
            />
          </Field>
          <Field label="State">
            {form.country === UnitedStates ? (
              <Select
                showSearch
                size="large"
                style={{ width: "100%" }}
                placeholder="State"
                value={form.state || undefined}
                onChange={(v) => update("state", v)}
                optionFilterProp="label"
                options={US_STATE_OPTIONS}
              />
            ) : (
              <Input
                placeholder="State"
                size="large"
                value={form.state}
                onChange={(e) => update("state", e.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="pi-row">
          <Field label="Zip/Postal Code">
            <Input
              placeholder="Zip/Postal Code"
              size="large"
              value={form.zip}
              onChange={(e) => update("zip", e.target.value)}
            />
          </Field>
          <Field label="Country">
            <CountryDropdown
              defaultValue={form.country}
              onChange={(v) => update("country", v)}
            />
          </Field>
        </div>

        <div className="pi-switch-row">
          <span className="pi-field-label">Suppress Street Address</span>
          <Switch
            checked={form.suppressAddress}
            onChange={(checked) => update("suppressAddress", checked)}
          />
        </div>

        <Field label="Display Address">
          <Input
            placeholder="e.g. 2nd Floor of Shopping Mall"
            size="large"
            value={form.displayAddress}
            onChange={(e) => update("displayAddress", e.target.value)}
          />
        </Field>

        <div className="pi-row">
          <Field label="Latitude">
            <Input
              placeholder="Latitude"
              size="large"
              value={form.latitude}
              onChange={(e) => update("latitude", e.target.value)}
            />
          </Field>
          <Field label="Longitude">
            <Input
              placeholder="Longitude"
              size="large"
              value={form.longitude}
              onChange={(e) => update("longitude", e.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">Contact</h3>

        <div className="pi-row">
          <Field label="Primary Phone">
            <Input
              placeholder="Primary Phone"
              size="large"
              value={form.primaryPhone}
              onChange={(e) => update("primaryPhone", e.target.value)}
            />
          </Field>
          <Field label="Secondary Phone">
            <Input
              placeholder="Secondary Phone"
              size="large"
              value={form.secondaryPhone}
              onChange={(e) => update("secondaryPhone", e.target.value)}
            />
          </Field>
        </div>

        <Field label="Email">
          <Input
            placeholder="john123@restaurant.com"
            size="large"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
          />
        </Field>

        <Field label="Website">
          <Input
            placeholder="Website"
            size="large"
            value={form.website}
            onChange={(e) => update("website", e.target.value)}
            onBlur={(e) => update("website", ensureHttps(e.target.value))}
          />
        </Field>

        <Field label="Website Display URL">
          <Input
            placeholder="Website Display URL"
            size="large"
            value={form.websiteDisplayUrl}
            onChange={(e) => update("websiteDisplayUrl", e.target.value)}
          />
        </Field>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">About Your Business</h3>

        <Field label="Business Description (Short)">
          <Input
            placeholder="Business Description (Short)"
            size="large"
            value={form.descriptionShort}
            onChange={(e) => update("descriptionShort", e.target.value)}
          />
        </Field>

        <Field label="Business Description (Long)">
          <TextArea
            placeholder="Business Description (Long)"
            rows={5}
            value={form.descriptionLong}
            onChange={(e) => update("descriptionLong", e.target.value)}
          />
        </Field>

        <Field label="Featured Message">
          <TextArea
            placeholder="Featured Message"
            rows={4}
            value={form.featuredMessage}
            onChange={(e) => update("featuredMessage", e.target.value)}
          />
        </Field>

        <div className="pi-categories">
          <span className="pi-field-label">Business Categories</span>
          <div className="pi-categories-list">
            {BUSINESS_CATEGORIES.map((cat) => (
              <Checkbox
                key={cat}
                checked={form.categories.includes(cat)}
                onChange={(e) => toggleInList("categories", cat, e.target.checked)}
              >
                {cat}
              </Checkbox>
            ))}
          </div>
        </div>

        <Field label="Opening Date (Where supported)">
          <DatePicker
            placeholder="Opening Date"
            size="large"
            className="pi-date-picker"
            value={form.openingDate}
            onChange={(d) => update("openingDate", d)}
            format="MMM D, YYYY"
          />
        </Field>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">Staff Bios</h3>

        {form.staffBios.map((bio, i) => (
          <div className="pi-staff-bio" key={i}>
            <div className="pi-staff-bio-head">
              <span className="pi-field-label">Staff Bio {i + 1}</span>
              {(form.staffBios.length > 1 ||
                bio.name.trim().length > 0 ||
                bio.bio.trim().length > 0) && (
                <button
                  type="button"
                  className="pi-staff-bio-remove"
                  onClick={() => removeStaffBio(i)}
                >
                  Remove
                </button>
              )}
            </div>
            <Input
              placeholder="Name"
              size="large"
              value={bio.name}
              onChange={(e) => updateStaffBio(i, "name", e.target.value)}
            />
            <TextArea
              placeholder={`Staff bio ${i + 1}`}
              rows={4}
              value={bio.bio}
              onChange={(e) => updateStaffBio(i, "bio", e.target.value)}
            />
          </div>
        ))}

        <Button className="pi-add-staff-bio" onClick={addStaffBio}>
          + Add Staff Bio
        </Button>
      </section>

      <footer className="pi-actions">
        {!hideDiscard && (
          <Button
            className="pi-discard-btn"
            size="large"
            onClick={discardChanges}
            disabled={saving || !dirty}
          >
            Discard Changes
          </Button>
        )}
        <Button
          type="primary"
          danger
          className="pi-save-btn"
          size="large"
          onClick={saveChanges}
          loading={saving}
          disabled={!dirty}
        >
          {saveLabel}
        </Button>
      </footer>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="pi-field">
      <span className="pi-field-label">{label}</span>
      {children}
    </label>
  );
}
